import React from 'react';
import { cn } from '@/utils/cn';
import { formatAccountId as formatGoogleAccountId } from '@/utils/googleAds';
import { formatAccountId as formatMetaAccountId } from '@/utils/metaAds';
import { PlatformBadge } from '@/components/features/CampaignCard/shared/PlatformBadge';

export interface OmniGoogleAccount {
  id: string;          // bare-digits Google customer ID
  name: string;
}

export interface OmniMetaAccount {
  id: string;          // act_<digits>
  businessName: string | null;
}

interface OmniAccountPickerProps {
  isOpen: boolean;
  googleAccounts: ReadonlyArray<OmniGoogleAccount>;
  metaAccounts: ReadonlyArray<OmniMetaAccount>;
  onSelectGoogle: (accountId: string) => void;
  onSelectMeta: (accountId: string) => void;
  onCancel: () => void;
}

/**
 * OmniAccountPicker — replaces the prior `PlatformChooserDialog`.
 *
 * Surfaces when the user clicks `+ Create Campaign` on the Account page with
 * 2+ linked ad accounts (across any mix of platforms). The user picks an
 * account; that selection determines which platform's wizard launches next
 * (the wizard then starts at Step 2 with the account pre-selected — Step 1's
 * account picker becomes unreachable for this invocation).
 *
 * The previous PlatformChooserDialog asked "which platform" first and the
 * wizard's Step 1 asked "which account" second — two clicks for what's
 * conceptually one decision. Conflating them as "which account" (and
 * inferring the platform from the account's intrinsic platform) is the
 * tighter UX. With 0 or 1 total accounts the picker is bypassed entirely.
 *
 * Account rows display:
 *   - business name (Meta) or account name (Google)
 *   - formatted account ID (`977198964917414` for Meta — strips `act_` via
 *     `utils/metaAds.formatAccountId`; `123-456-7890` for Google via
 *     `utils/googleAds.formatAccountId`)
 *   - platform badge (Google Ads / Meta Ads)
 */
export const OmniAccountPicker: React.FC<OmniAccountPickerProps> = ({
  isOpen,
  googleAccounts,
  metaAccounts,
  onSelectGoogle,
  onSelectMeta,
  onCancel,
}) => {
  if (!isOpen) return null;

  const totalAccounts = googleAccounts.length + metaAccounts.length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Select an ad account"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full my-4 sm:my-8 p-6 relative">
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1"
          aria-label="Close"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2 className="text-xl font-bold text-gray-900 mb-2 pr-8">
          Select an Account
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Which ad account is this campaign for? You have {totalAccounts} linked.
        </p>

        <ul
          aria-label="Linked ad accounts"
          className="space-y-2 max-h-[60vh] overflow-y-auto list-none p-0 m-0"
        >
          {googleAccounts.map((acc) => (
            <AccountRow
              key={`google:${acc.id}`}
              displayName={acc.name}
              displayId={formatGoogleAccountId(acc.id)}
              platform="Google Ads"
              platformBadgeClass="bg-green-100 text-green-800"
              onSelect={() => onSelectGoogle(acc.id)}
            />
          ))}
          {metaAccounts.map((acc) => (
            <AccountRow
              key={`meta:${acc.id}`}
              displayName={acc.businessName ?? 'Unnamed business'}
              displayId={formatMetaAccountId(acc.id)}
              platform="Meta Ads"
              platformBadgeClass="bg-blue-100 text-blue-800"
              onSelect={() => onSelectMeta(acc.id)}
            />
          ))}
        </ul>

        <button
          type="button"
          onClick={onCancel}
          className="mt-4 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors w-full text-center"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

interface AccountRowProps {
  displayName: string;
  displayId: string;
  platform: 'Google Ads' | 'Meta Ads';
  platformBadgeClass: string;
  onSelect: () => void;
}

const AccountRow: React.FC<AccountRowProps> = ({
  displayName,
  displayId,
  platform,
  platformBadgeClass,
  onSelect,
}) => {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'w-full text-left p-4 border-2 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
          'border-gray-200 hover:bg-gray-50 bg-white',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">
              {displayName}
            </div>
            <div className="text-xs text-gray-500 mt-1 font-mono">{displayId}</div>
          </div>
          {/* Icons + label badge. The icons are the same shared PlatformBadge
              asset campaign cards use (CampaignCard/shared/PlatformBadge.tsx),
              sized down so they fit alongside the small text pill. */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <PlatformBadge platform={platform} heightPx={18} />
            <span
              className={cn(
                'text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap',
                platformBadgeClass,
              )}
            >
              {platform}
            </span>
          </div>
        </div>
      </button>
    </li>
  );
};
